// Minimal reactive store: hold the app state, notify subscribers on change.
import type { ChatConfig, ChatState, LanguageOption, RecPhase } from './types';

export interface ChatStoreState {
  state: ChatState;
  config: ChatConfig;
  selectedLang: LanguageOption;
  inputValue: string;
  /** Caption shown in the `response` state (the answer transcript). */
  responseTranscript: string;
  /** Live word-by-word STT transcript shown while `listening` (empty = none yet). */
  liveTranscript: string;
  /** Where the recording stands while `listening` (see RecPhase). */
  recPhase: RecPhase;
  /** When set, a blocking full-screen error message is shown. */
  error: string | null;
  /** When true, controls are disabled (e.g. the daily limit has been reached). */
  locked: boolean;
  /** When set, forces the caption text (loading / limit / status messages). */
  overrideCaption: string | null;
}

type Listener = (s: ChatStoreState) => void;

export class ChatStore {
  private listeners = new Set<Listener>();
  state: ChatStoreState;

  constructor(initial: ChatStoreState) {
    this.state = initial;
  }

  /** Subscribe and receive the current state immediately. Returns an unsubscribe fn. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  set(patch: Partial<ChatStoreState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }
}

/** The caption text for the current state/language. */
export function captionFor(s: ChatStoreState): string {
  if (s.overrideCaption !== null) return s.overrideCaption;
  const c = s.selectedLang.captions;
  const first = (arr: string[]) => arr[0] ?? '';
  switch (s.state) {
    case 'wait':
    case 'keyboard':
      return first(c.wait);
    case 'listening':
      return first(c.listening);
    case 'processing':
      return first(c.processing);
    case 'response':
      return s.responseTranscript;
    case 'eager':
      return first(c.eager);
    case 'loading':
    case 'policy':
      return '';
  }
}

/** Controls (mic/send/input) are interactive only in these states. */
export function controlsActive(state: ChatState): boolean {
  return state === 'wait' || state === 'eager' || state === 'keyboard' || state === 'listening';
}
